import {
  CheckCircleOutline as CheckCircleIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import { Tooltip, useMediaQuery } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import './ServiceBox.scss';

const ServiceBox = ({
  avatar = { shape: 'square', color1: '#FFFFFF', color2: '#FFFFFF' }, // Fallback avatar object
  title,
  description,
  className,
  onClick,
  amount,
  supply,
  note,
  station,
  disableTooltip, // Prop to control tooltips
}) => {
  const { shape, color1, color2 } = avatar; // Destructure the avatar object
  const shapes = {
    square: <rect width="60" height="60" fill={color1} />,
    triangle: <polygon points="0,0 60,0 0,60" fill={color1} />,
    halfTriangle: (
      <>
        <polygon points="0,0 60,0 0,60" fill={color1} />
        <polygon points="60,60 60,0 0,60" fill={color2} />
      </>
    ),
  };

  const [openTooltip, setOpenTooltip] = useState(false);
  const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');

  // Tooltip content based on the condition
  const tooltipContent = (
    <>
      {amount !== 0 && amount !== '' && (
        <div className="flex items-center gap-1">
          <SellOutlinedIcon sx={{ fontSize: '1.2rem' }} />
          <div className="font-bold text-lg">{amount}</div>
        </div>
      )}
      {supply !== 0 && <div>Supply: {supply}</div>}
      {station === ' ' ? '...' : <div>Station: {station}</div>}
      {note && note !== '' && note !== ' ' && <div>Note: {note}</div>}
    </>
  );

  // Determine if the tooltip should be disabled
  const shouldDisableTooltip = disableTooltip || (!supply && !amount && !note);

  // Handle touch events to manually open/close tooltip on mobile devices
  const handleTouchStart = () => {
    setOpenTooltip(true);
  };

  const handleTouchEnd = () => {
    setOpenTooltip(false);
  };

  const content = (
    <div
      className={`service-box-container ${className}`}
      onClick={onClick}
      onTouchStart={isTouchDevice ? handleTouchStart : null}
      onTouchEnd={isTouchDevice ? handleTouchEnd : null}
    >
      <div className={`service-box ${amount !== 0 ? 'has-amount' : ''}`}>
        {amount > 0 && (
          <div className="red-circle">
            <CheckCircleIcon sx={{ fontSize: 14, color: '#ff8a80' }} />
          </div>
        )}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 60" width="60" height="60">
          {shapes[shape]}
          <text x="5" y="19" className="service-title">
            {title}
          </text>
          <text x="5" y="34" className="service-description">
            {description}
          </text>
        </svg>

        {typeof note === 'string' && note.trim() !== '' && (
          <div className="info-icon">
            <InfoIcon sx={{ fontSize: 14, color: '#649ac6' }} />
          </div>
        )}
      </div>
    </div>
  );

  // Render with or without Material-UI Tooltip based on the condition and device type
  return shouldDisableTooltip ? (
    content
  ) : isTouchDevice ? (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      open={openTooltip}
      onClose={handleTouchEnd}
    >
      {content}
    </Tooltip>
  ) : (
    <Tooltip title={tooltipContent} arrow placement="top">
      {content}
    </Tooltip>
  );
};

ServiceBox.propTypes = {
  avatar: PropTypes.shape({
    shape: PropTypes.string,
    color1: PropTypes.string,
    color2: PropTypes.string, // color2 is optional
  }).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func,
  amount: PropTypes.number,
  supply: PropTypes.number,
  note: PropTypes.string,
  station: PropTypes.string,
  disableTooltip: PropTypes.bool, // Prop for disabling tooltips
};

export default ServiceBox;
